import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import type { ProdutoEstoqueRegistro } from '@/features/produto/util/estoque';
import { getProdutoEstoqueId, getProdutoEstoquesParaVenda } from '@/features/produto/util/estoque';
import { DATE_FORMAT } from '@/std/const/date';
import type { Produto } from '../../types/Produto';
import type { ProdutoPacoteBarra } from '../../types/ProdutoPacoteBarra';
import { type ProdutoLote, ProdutoLoteStatus } from '../../types/ProdutoLote';

dayjs.extend(customParseFormat);

export function getProdutoSelectEstoqueDescricao(estoque: ProdutoEstoqueRegistro): string {
    const id = getProdutoEstoqueId(estoque);
    return estoque.estoque?.est_descricao ?? (id == null ? 'Estoque padrão' : `Local ${id}`);
}

export function getProdutoSelectEstoques(
    produto: Produto,
    lote: ProdutoLote | null = null,
): ProdutoEstoqueRegistro[] {
    return getProdutoEstoquesParaVenda(produto, lote).filter(
        (saldo) => getProdutoEstoqueId(saldo) != null,
    );
}

export function getProdutoSelectLocalAutomatico(
    produto: Produto,
    pacoteBarra: ProdutoPacoteBarra | null = null,
    lote: ProdutoLote | null = null,
): ProdutoEstoqueRegistro | null {
    const estoques = getProdutoSelectEstoques(produto, lote);

    if (estoques.length === 1) {
        return estoques[0] ?? null;
    }

    const padraoId =
        pacoteBarra?.pcb_estoque_idpk ??
        produto.produto_estoque?.find((saldo) => saldo.pre_padrao === 'S')?.pre_estoque_idpk;

    return estoques.find((saldo) => getProdutoEstoqueId(saldo) === padraoId) ?? null;
}

export function getProdutoSelectLotes(produto: Produto): ProdutoLote[] {
    return (produto.lote ?? []).filter((lote) => lote.prl_status !== ProdutoLoteStatus.Encerrado);
}

export function getProdutoSelectLoteAutomatico(produto: Produto): ProdutoLote | null {
    if (produtoTemEtapaLote(produto)) {
        return null;
    }

    const lotes = getProdutoSelectLotes(produto).map((lote) => {
        const vencimento = dayjs(lote.prl_data_vencimento, DATE_FORMAT, true);
        return {
            lote,
            saldo: getProdutoEstoquesParaVenda(produto, lote).reduce(
                (total, estoque) =>
                    total +
                    ('ple_estoque_disponivel' in estoque ? estoque.ple_estoque_disponivel : 0),
                0,
            ),
            vencimento: vencimento.isValid() ? vencimento.valueOf() : Infinity,
        };
    });

    lotes.sort(
        (a, b) =>
            Number(b.saldo > 0) - Number(a.saldo > 0) ||
            a.vencimento - b.vencimento ||
            b.saldo - a.saldo,
    );

    return lotes[0]?.lote ?? null;
}

export function produtoTemEtapaLote(produto: Produto): boolean {
    return produto.pro_lote_solicitar_venda === 'S' && getProdutoSelectLotes(produto).length > 1;
}

export function produtoPrecisaSelecao(produto: Produto): boolean {
    const lote = getProdutoSelectLoteAutomatico(produto);

    return (
        !!produto.produto_pacote_barra?.length ||
        produtoTemEtapaLote(produto) ||
        (getProdutoSelectEstoques(produto, lote).length > 1 &&
            !getProdutoSelectLocalAutomatico(produto, null, lote))
    );
}
